import Link from '@mui/material/Link';
import { TextV2 } from '@insights-ltd/design-library/components';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import PermissionGuard from 'components/PermissionGuard';
import AddIcon from 'components/Svgs/icons/Add';
import TransferIcon from 'components/Svgs/icons/DataTransferHorizontal';
import { styled } from '@mui/material';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';

export type WalletProps = {
  availableUnits: number;
  organisationId?: string;
  practitionerId?: string;
  isMyWallet?: boolean;
};

const StyledWallet = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(spacingSizeMap.S)} ${theme.spacing(
    spacingSizeMap.M,
  )}`,
  background: theme.palette.primary.light,
  borderRadius: '4px',
  [theme.breakpoints.up('md')]: {
    marginLeft: '80px',
  },
}));

const StyledWalletTitle = styled(TextV2)(({ theme }) => ({
  paddingBottom: theme.spacing(spacingSizeMap.XS),
}));

const StyledAvailableUnits = styled(TextV2)(({ theme }) => ({
  marginLeft: `${theme.spacing(spacingSizeMap.XS)} !important`,
  paddingBottom: theme.spacing(spacingSizeMap.M),
}));

interface StyledUnitsLinkProps {
  transfer?: boolean;
}

const StyledUnitsLink = styled(TextV2, {
  shouldForwardProp: (prop) => prop !== 'transfer',
})<StyledUnitsLinkProps>(({ transfer = false, theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingTop: transfer
    ? theme.spacing(spacingSizeMap.S)
    : theme.spacing(spacingSizeMap.XS),
  fontFamily: `${theme.typography.fontFamily} !important`,
  fontWeight: `${theme.typography.fontWeightBold} !important`,
  fontSize: '0.875rem !important',
}));

const StyledUnitsLinkIcon = styled('span')(({ theme }) => ({
  marginLeft: `${theme.spacing(spacingSizeMap.XS)} !important`,
}));

const Wallet = ({
  availableUnits,
  organisationId,
  practitionerId,
  isMyWallet = false,
}: WalletProps) => {
  const { t } = useTranslation();

  const titleKey = isMyWallet
    ? 'ui.event-management.wallet.title.my-wallet'
    : 'ui.event-management.wallet.title';

  return (
    <StyledWallet>
      <StyledWalletTitle component="p" variant="bodyBold">
        {t(titleKey)}
      </StyledWalletTitle>
      <TextV2 component="span" data-testid="available-units" variant="h3">
        {availableUnits}
      </TextV2>
      <StyledAvailableUnits
        component="span"
        variant="bodySmall"
        color="textSecondary"
      >
        {t('ui.event-management.wallet.balance', {
          count: availableUnits,
        })}
      </StyledAvailableUnits>
      <PermissionGuard
        requiredPermissions={[
          {
            action: 'Unit_Transfer',
            scope: 'Local',
          },
        ]}
      >
        <Link
          component={RouterLink}
          to="/account/transfer-units"
          state={
            isMyWallet ? { organisationId } : { practitionerId, organisationId }
          }
        >
          <StyledUnitsLink component="p" transfer color="primary">
            <TransferIcon />
            <StyledUnitsLinkIcon>
              {t('ui.event-management.wallet.transfer-units')}
            </StyledUnitsLinkIcon>
          </StyledUnitsLink>
        </Link>
      </PermissionGuard>
      <PermissionGuard
        requiredPermissions={[
          {
            action: 'Unit_Update',
            scope: 'Global',
          },
        ]}
      >
        <Link
          component={RouterLink}
          to="/account/manage-units"
          state={{ practitionerId, organisationId }}
        >
          <StyledUnitsLink component="p" color="primary">
            <AddIcon />
            <StyledUnitsLinkIcon>
              {t('ui.event-management.wallet.manage-units')}
            </StyledUnitsLinkIcon>
          </StyledUnitsLink>
        </Link>
      </PermissionGuard>
    </StyledWallet>
  );
};

export default Wallet;
